import React, { useState } from 'react';
import ExpensesFlow from './ExpensesFlow';
import styled from '@emotion/styled';
import { useI18n, useDispatch, useCurrency, useButton } from '~Hooks';
import { RouteComponentProps } from 'react-router-dom';
import { StyledTable } from '~Styles/table';
import { addToast } from '~/actions';
import { gql, useQuery } from '@apollo/client';
import { GetAllExpenses } from './__queries__';
import { DateTime } from 'luxon';
import BackTopBar from '~ComponentsImproved/BackTopBar';
import { BlueButton, Box, GreenButton } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Loader from '~ComponentsImproved/Loader';
import { keyframes } from '@emotion/react';
import Heading from '~ComponentsImproved/Typography/Heading';

const GET_ALL_EXPENSES = gql`
  query GetAllExpenses($venueId: ID!, $first: Int, $skip: Int, $orderBy: ExpenseOrderByInput) {
    venue: Venue(id: $venueId) {
      id
      expenses(first: $first, skip: $skip, orderBy: $orderBy) {
        id
        createdAt
        amount
        staff {
          id
          name
        }
      }
    }
  }
`;

const pulseAnimation = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 0.3; }
  100% { opacity: 0.6; }
`;

const BlurryBox = styled.div<{ width?: string; height?: string }>`
  width: ${({ width }) => (width ? width : '100px')};
  height: ${({ height }) => (height ? height : '16px')};
  background-color: var(--colorBlueGray200);
  border-radius: 4px;
  animation: ${pulseAnimation} 2s ease-in-out infinite;
`;

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  color: var(--colorWhite);
  min-height: calc(100vh - 64px);
`;

const ScrollWrapper = styled.div`
  overflow-y: auto;
  height: 100%;
  flex: 1;
  margin-top: 4px;
  padding: 20px 40px;
  box-sizing: border-box;
`;

const StyledBox = styled(Box)`
  background-color: var(--colorWhite);
  border-radius: 8px;
  width: 700px;
`;

const LoadMoreButton = styled(BlueButton)`
  width: 200px;
`;

type Props = RouteComponentProps<{ id: string }>;

const AdyenExpense: React.FunctionComponent<Props> = ({ match }) => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const currency = useCurrency();

  const venueId = match.params.id;
  const [skip, setSkip] = useState<number>(0);

  const [showExpenseFlow, setShowExpenseFlow] = useState<boolean>(false);

  const expenseQuery = useQuery<GetAllExpenses>(GET_ALL_EXPENSES, {
    variables: { venueId: venueId, first: 25, skip: 0, orderBy: 'createdAt_DESC' },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const expenses = expenseQuery.data?.venue?.expenses || [];

  const handleLoadMore = () => {
    const newSkip = skip + 25;
    expenseQuery.fetchMore({
      variables: { skip: newSkip },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          ...prev,
          venue: {
            ...prev.venue,
            expenses: [...prev.venue.expenses, ...fetchMoreResult.venue.expenses],
          },
        };
      },
    });
    setSkip(newSkip);
  };

  const startExpenseFlowButton = useButton({
    onClick: () => {
      dispatch(addToast(i18n('Expenses.ExpenseFlowStarted'), true));
      setShowExpenseFlow(true);
    },
  });

  const loadMoreButton = useButton({
    onClick: () => {
      handleLoadMore();
    },
    disabled: expenseQuery.loading,
  });

  return (
    <>
      <Wrapper>
        <BackTopBar title={i18n('Expenses.HeaderTitle')} />

        <ScrollWrapper>
          <Box fluid display="flex" flexDirection="column" alignItems="center" gap="medium">
            <StyledBox
              display="flex"
              justifyContent="space-between"
              padding="medium"
              alignItems="center">
              <Heading level={3} bottom={0}>
                {i18n('Expenses.StartExpenseFlowDescription')}
              </Heading>

              <GreenButton {...startExpenseFlowButton}>
                {i18n('Expenses.StartExpenseFlowCTA')}
              </GreenButton>
            </StyledBox>

            {expenseQuery.loading && skip === 0 && (
              <StyledTable>
                <table>
                  <thead>
                    <tr>
                      <th>{i18n('Expenses.Date')}</th>
                      <th>{i18n('Expenses.Sum')}</th>
                      <th>{i18n('Expenses.Staff')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <tr key={`skeleton-${index}`}>
                        <td>
                          <BlurryBox width="120px" height="16px" />
                        </td>
                        <td>
                          <BlurryBox width="80px" height="16px" />
                        </td>
                        <td>
                          <BlurryBox width="100px" height="16px" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </StyledTable>
            )}

            {expenses && expenses.length === 0 && !expenseQuery.loading ? (
              <StyledBox padding="medium" display="flex" justifyContent="center">
                <Heading level={3} bottom={16} top={16}>
                  {i18n('Expenses.NoExpensesFound')}
                </Heading>
              </StyledBox>
            ) : expenses.length > 0 ? (
              <StyledTable>
                <table>
                  <thead>
                    <tr>
                      <th>{i18n('Expenses.Date')}</th>
                      <th>{i18n('Expenses.Sum')}</th>
                      <th>{i18n('Expenses.Staff')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenseQuery.error && (
                      <tr>
                        <td colSpan={3}>Error: {expenseQuery.error.message}</td>
                      </tr>
                    )}
                    {expenses &&
                      expenses.length > 0 &&
                      expenses.map((expense) => (
                        <tr key={expense.id}>
                          <td>
                            <span>
                              {DateTime.fromISO(expense.createdAt).toFormat('yyyy-MM-dd')}
                            </span>
                            <span style={{ marginLeft: '0.5rem', color: '#6b7280' }}>
                              {DateTime.fromISO(expense.createdAt).toFormat('HH:mm:ss')}
                            </span>
                          </td>
                          <td className="amount positive">{currency.format(expense.amount)}</td>
                          <td>{expense.staff.name}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </StyledTable>
            ) : null}

            {expenseQuery.data && expenses.length % 25 === 0 && (
              <LoadMoreButton
                {...loadMoreButton}
                disabled={expenseQuery.loading}
                marginBottom="medium">
                {expenseQuery.loading ? (
                  <Loader size="sm" color="var(--colorWhite)" />
                ) : (
                  i18n('Expense.LoadMore')
                )}
              </LoadMoreButton>
            )}
          </Box>
        </ScrollWrapper>
      </Wrapper>

      {showExpenseFlow && (
        <ExpensesFlow
          venueId={venueId}
          onComplete={() => setShowExpenseFlow(false)}
          onFail={() => setShowExpenseFlow(false)}
        />
      )}
    </>
  );
};

export default AdyenExpense;
